/**
 * Pure spawn target policies (Track Z / TOP-12, TOP-13).
 * Click places geometry; this module chooses friend/enemy (+ optional inbound).
 */

export type SpawnTargetPick =
  | "random_global"
  | "nearest"
  | "second_nearest"
  | "random_local"
  | "farthest_local";

export type SpawnInbound =
  | "none"
  | "nearest_friend"
  | "nearest_enemy"
  | "nearest_both";

export type SpawnPolicy = {
  friend: SpawnTargetPick;
  enemy: SpawnTargetPick;
  /** Radius for *_local picks */
  localRadius: number;
  inbound: SpawnInbound;
  /** When friend === enemy and n0 ≥ 2, force enemy distinct */
  avoidFriendEqEnemy: boolean;
};

export type SpawnInboundEdit = {
  agent: number;
  role: "friend" | "enemy";
};

export type SpawnResultMeta = {
  friend: number;
  enemy: number;
  inbound: SpawnInboundEdit[];
  /** True when a local pick fell back to global */
  usedGlobalFallback: boolean;
};

export type SpawnPickCtx = {
  positions: Float64Array;
  dim: number;
  /** Existing population size (before append) */
  n0: number;
  /** World position of newcomer */
  w: ArrayLike<number>;
  localRadius: number;
  forbidSelf: boolean;
  /** Optional RNG — required for random_* picks */
  int: (maxExclusive: number) => number;
};

export function defaultSpawnPolicy(localRadius = 0.35): SpawnPolicy {
  return {
    friend: "random_global",
    enemy: "random_global",
    localRadius,
    inbound: "none",
    avoidFriendEqEnemy: true,
  };
}

function dist2(
  positions: Float64Array,
  dim: number,
  j: number,
  w: ArrayLike<number>,
): number {
  let s = 0;
  const base = j * dim;
  for (let d = 0; d < dim; d++) {
    const a = positions[base + d]!;
    const b = w[d] ?? 0;
    const dd = a - b;
    s += dd * dd;
  }
  return s;
}

/** Sorted by distance ascending: [{j, d2}, ...] */
export function rankByDistance(ctx: SpawnPickCtx): { j: number; d2: number }[] {
  const { positions, dim, n0, w } = ctx;
  const out: { j: number; d2: number }[] = [];
  for (let j = 0; j < n0; j++) {
    out.push({ j, d2: dist2(positions, dim, j, w) });
  }
  out.sort((a, b) => a.d2 - b.d2 || a.j - b.j);
  return out;
}

function localSet(
  ranked: { j: number; d2: number }[],
  radius: number,
): number[] {
  const r2 = radius * radius;
  const out: number[] = [];
  for (const row of ranked) {
    if (row.d2 <= r2) out.push(row.j);
  }
  return out;
}

function randomAmong(indices: number[], int: (m: number) => number): number {
  if (indices.length === 0) return 0;
  return indices[int(indices.length)]!;
}

function randomGlobal(n0: number, int: (m: number) => number): number {
  if (n0 <= 0) return 0;
  return int(n0);
}

/**
 * Pick one target index under `kind`.
 * Sets `fallback` true when local modes had an empty ball.
 */
export function pickTarget(
  kind: SpawnTargetPick,
  ctx: SpawnPickCtx,
  ranked: { j: number; d2: number }[],
  fallback: { used: boolean },
): number {
  const { n0, localRadius, int } = ctx;
  if (n0 <= 0) return 0;

  switch (kind) {
    case "random_global":
      return randomGlobal(n0, int);
    case "nearest":
      return ranked[0]?.j ?? 0;
    case "second_nearest":
      if (ranked.length >= 2) return ranked[1]!.j;
      return ranked[0]?.j ?? 0;
    case "random_local": {
      const loc = localSet(ranked, localRadius);
      if (loc.length === 0) {
        fallback.used = true;
        return randomGlobal(n0, int);
      }
      return randomAmong(loc, int);
    }
    case "farthest_local": {
      const loc = localSet(ranked, localRadius);
      if (loc.length === 0) {
        fallback.used = true;
        // global farthest
        return ranked[ranked.length - 1]?.j ?? 0;
      }
      // farthest among local = last in ranked that is still local
      const r2 = localRadius * localRadius;
      for (let i = ranked.length - 1; i >= 0; i--) {
        if (ranked[i]!.d2 <= r2) return ranked[i]!.j;
      }
      return loc[loc.length - 1]!;
    }
    default:
      return randomGlobal(n0, int);
  }
}

function distinctFrom(
  preferred: number,
  friend: number,
  ranked: { j: number; d2: number }[],
  n0: number,
): number {
  if (preferred !== friend) return preferred;
  for (const row of ranked) {
    if (row.j !== friend) return row.j;
  }
  for (let j = 0; j < n0; j++) {
    if (j !== friend) return j;
  }
  return preferred;
}

export function resolveSpawnLinks(
  ctx: SpawnPickCtx,
  policy: SpawnPolicy,
): SpawnResultMeta {
  const n0 = ctx.n0;
  const ranked = rankByDistance(ctx);
  const fallback = { used: false };

  const pickCtx: SpawnPickCtx = {
    ...ctx,
    localRadius: policy.localRadius > 0 ? policy.localRadius : ctx.localRadius,
  };

  let friend = pickTarget(policy.friend, pickCtx, ranked, fallback);
  let enemy = pickTarget(policy.enemy, pickCtx, ranked, fallback);

  if (policy.avoidFriendEqEnemy && n0 >= 2 && friend === enemy) {
    enemy = distinctFrom(
      pickTarget("second_nearest", pickCtx, ranked, fallback),
      friend,
      ranked,
      n0,
    );
    if (friend === enemy) {
      enemy = distinctFrom(enemy, friend, ranked, n0);
    }
  }

  const inbound: SpawnInboundEdit[] = [];
  if (n0 >= 1 && policy.inbound !== "none") {
    const nearest = ranked[0]?.j ?? 0;
    const second = ranked[1]?.j ?? nearest;
    if (
      policy.inbound === "nearest_friend" ||
      policy.inbound === "nearest_both"
    ) {
      inbound.push({ agent: nearest, role: "friend" });
    }
    if (policy.inbound === "nearest_enemy") {
      inbound.push({ agent: nearest, role: "enemy" });
    }
    if (policy.inbound === "nearest_both" && n0 >= 2) {
      inbound.push({ agent: second, role: "enemy" });
    } else if (policy.inbound === "nearest_both" && n0 === 1) {
      // only friend inbound already added
    }
  }

  return {
    friend,
    enemy,
    inbound,
    usedGlobalFallback: fallback.used,
  };
}
